import type { ReactNode } from "react";
import { Outlet, createFileRoute } from "@tanstack/react-router";

import { AppErrorBoundary } from "@/components/shared/app-error-boundary";
import { SidebarInset, SidebarProvider } from "@/components/ui/sidebar";
import { RouteGuard } from "@/components/auth/route-guard";
import { AppHeader } from "@/features/dashboard/components/header";
import { AppSidebar } from "@/features/dashboard/components/sidebar";
import { AuthProvider } from "@/providers/auth.provider";
import { WorkspaceAppearanceProvider } from "@/providers/workspace-appearance.provider";

/**
 * The operator shell (redesign step 5): platform operations get their own home under
 * /ops/* with an ops-only sidebar, instead of swapping personas inside the client
 * workspace shell. Acting-as-a-tenant work still happens under /dashboard/*.
 */
function OpsLayout({ children }: { children?: ReactNode }) {
  return (
    <AuthProvider>
      <WorkspaceAppearanceProvider className="contents">
        <SidebarProvider defaultOpen>
          <div className="console-shell w-full" role="application" aria-label="Operations layout">
            <a
              href="#main-content"
              className="sr-only rounded-md px-4 py-2 text-sm focus:not-sr-only focus:fixed focus:left-2 focus:top-2 focus:z-50 focus:bg-primary focus:text-primary-foreground"
            >
              Skip to main content
            </a>
            <RouteGuard>
              <AppSidebar navVariant="ops" />
              <SidebarInset>
                <AppHeader />
                <main
                  id="main-content"
                  className="flex w-full flex-col gap-4 px-4 pb-8 pt-3 sm:px-6 lg:px-8"
                  role="main"
                  aria-label="Operations content"
                >
                  {children ?? <Outlet />}
                </main>
              </SidebarInset>
            </RouteGuard>
          </div>
        </SidebarProvider>
      </WorkspaceAppearanceProvider>
    </AuthProvider>
  );
}

export const Route = createFileRoute("/(app)/ops")({
  component: OpsLayout,
  errorComponent: AppErrorBoundary,
});
